import { useEffect, useRef, useState } from 'react';
import {
  RiAddLine,
  RiCloseLine,
  RiCursorLine,
  RiFullscreenLine,
  RiLayoutLeft2Line,
  RiLayoutRight2Line,
  RiNodeTree,
  RiPauseLine,
  RiPlayLine,
  RiSkipBackLine,
  RiSkipForwardLine,
  RiStickyNoteLine,
  RiSubtractLine,
} from 'react-icons/ri';
import { CanvasRenderer } from '../canvas/renderer/CanvasRenderer';
import { DomRenderer } from '../canvas/renderer/DomRenderer';
import { GfxController } from '../canvas/core/GfxController';
import { elementToNode, nodesToElements } from '../canvas/persistence';
import { buildIntelGraph } from '../canvas/intelLayout';
import { useCanvasStore } from '../canvas/canvasStore';
import { useStore } from '../state/store';
import type { GfxElement } from '../canvas/core/types';
import { Toolbar } from './Toolbar';
import { Minimap } from './Minimap';
import { LinkSuggestions, WallAiDock, WallCasePlate, WallStatusBar, WallTimelineRibbon } from './wall/WallHud';
import { DRAG_MIME, readDragPayload, type DragPayload } from '../canvas/drag';
import { sendBoardBeacon } from '../state/api';
import { buildTourRoute } from '../canvas/tour';
import { Button } from './ui/button';

/** 会话级画布缓存：避免 StrictMode 重新挂载时丢失未保存改动。 */
let boardCache: { engagementId: string; elements: GfxElement[] } | null = null;


export function CanvasStage() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLCanvasElement | null>(null);
  const annotationRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const domHostRef = useRef<HTMLDivElement | null>(null);
  const controllerRef = useRef<GfxController | null>(null);
  const savedIdsRef = useRef<Set<string>>(new Set());
  const loadedEngagementRef = useRef<string | null>(null);
  const saveTimerRef = useRef<number | null>(null);
  const [ready, setReady] = useState(false);
  const [empty, setEmpty] = useState(true);

  const setController = useCanvasStore(state => state.setController);
  const bump = useCanvasStore(state => state.bump);
  const liveController = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);
  void revision;

  const activeEngagementId = useStore(state => state.activeEngagementId);
  const boardLoadedFor = useStore(state => state.boardLoadedFor);
  const boardStyle = useStore(state => state.boardStyle);
  const panels = useStore(state => state.panels);
  const togglePanel = useStore(state => state.togglePanel);
  const hud = useStore(state => state.hud);
  const preview = useStore(state => state.preview);
  const readOnlyView = useStore(state => state.readOnlyView);
  const myRole = useStore(state => state.engagements.find(e => e.id === state.activeEngagementId)?.myRole);
  const canWrite = myRole !== 'viewer';
  const pendingTour = useStore(state => state.pendingTour);
  const setPreview = useStore(state => state.setPreview);
  const [tourSpeed, setTourSpeed] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    const annotation = annotationRef.current;
    const overlay = overlayRef.current;
    const domHost = domHostRef.current;
    if (!container || !content || !annotation || !overlay || !domHost) return;

    const controller = new GfxController(container);
    const canvasRenderer = new CanvasRenderer(content, annotation, overlay, controller);
    const domRenderer = new DomRenderer(domHost, controller);

    controller.renderer = {
      render: () => canvasRenderer.render(),
      resize: (w, h) => canvasRenderer.resize(w, h),
      syncDom: () => domRenderer.sync(),
    };

    controllerRef.current = controller;
    setController(controller);

    const resize = () => {
      const rect = container.getBoundingClientRect();
      controller.resize(rect.width, rect.height);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);

    /** 组装一次完整画布快照（元素 + 视口 + 板面样式 + 待删除）。 */
    const buildPayload = () => {
      const state = useStore.getState();
      const elements = controller.surface.all();
      const currentIds = new Set(elements.map(el => el.id));
      const deletes = [...savedIdsRef.current].filter(id => !currentIds.has(id));
      savedIdsRef.current = currentIds;
      return {
        viewport: {
          x: controller.viewport.viewportX,
          y: controller.viewport.viewportY,
          zoom: controller.viewport.zoom,
        },
        style: state.boardStyle,
        upserts: elements.map(elementToNode),
        deletes,
      };
    };

    /** 立即落盘。 */
    const flush = () => {
      const state = useStore.getState();
      if (!state.activeEngagementId) return;
      void state.saveBoard(buildPayload());
    };

    /** 页面关闭/切后台时的兜底落盘（sendBeacon，不受请求被中断影响）。 */
    const flushBeacon = () => {
      const state = useStore.getState();
      if (!state.activeEngagementId) return;
      if (state.boardSave.state === 'saved' || state.boardSave.state === 'clean') return;
      sendBoardBeacon(state.activeEngagementId, buildPayload());
      useStore.getState().setBoardSave({ state: 'saved', at: new Date().toISOString() });
    };

    const scheduleSave = () => {
      useStore.getState().setBoardSave({ state: 'dirty' });
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(flush, 1200);
    };

    const unsubscribe = controller.onChange(() => {
      bump();
      setEmpty(controller.surface.size === 0);
      scheduleSave();
    });

    // 关闭页签 / 切到后台时，把未保存的改动立刻写回
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (saveTimerRef.current !== null) {
          window.clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
        }
        flushBeacon();
      }
    };
    window.addEventListener('pagehide', flushBeacon);
    window.addEventListener('beforeunload', flushBeacon);
    document.addEventListener('visibilitychange', onVisibility);

    setReady(true);
    return () => {
      const currentEngagement = useStore.getState().activeEngagementId;
      if (currentEngagement && controller.surface.size > 0) {
        boardCache = { engagementId: currentEngagement, elements: controller.surface.all() };
      }
      unsubscribe();
      observer.disconnect();
      window.removeEventListener('pagehide', flushBeacon);
      window.removeEventListener('beforeunload', flushBeacon);
      document.removeEventListener('visibilitychange', onVisibility);
      controller.destroy();
      domRenderer.destroy();
      if (saveTimerRef.current !== null) window.clearTimeout(saveTimerRef.current);
      loadedEngagementRef.current = null;
      setController(null);
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 软木板 / 纸面 / 网格背景切换 ────────────────────────
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !ready) return;
    if (controller.boardStyle !== boardStyle) controller.setBoardStyle(boardStyle);
  }, [boardStyle, ready]);

  // ── 全屏预览时为只读；退出预览则停止巡航 ──────────────
  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !ready) return;
    controller.setReadonly(preview || readOnlyView || !canWrite);
    if (!preview) controller.stopTour();
  }, [preview, readOnlyView, canWrite, ready]);

  // ── 启动自动巡航 ─────────────────────────────────────
  const launchTour = () => {
    const controller = controllerRef.current;
    if (!controller) return;
    const state = useStore.getState();
    const stops = buildTourRoute({
      elements: controller.surface.all(),
      targets: state.targets,
      entities: state.entities,
    });
    if (stops.length === 0) {
      state.toast('error', '墙面暂无卡片，无法巡航');
      return;
    }
    controller.startTour(stops, tourSpeed);
  };

  useEffect(() => {
    if (!pendingTour || !ready) return;
    useStore.setState({ pendingTour: false });
    setTimeout(launchTour, 260);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTour, ready]);

  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || !activeEngagementId) return;
    if (boardLoadedFor !== activeEngagementId) return;
    if (loadedEngagementRef.current === activeEngagementId) return;
    loadedEngagementRef.current = activeEngagementId;

    const { boardNodes, boardViewport } = useStore.getState();
    const cached = boardCache?.engagementId === activeEngagementId ? boardCache.elements : null;
    const stored = cached ?? nodesToElements(boardNodes);
    boardCache = null;

    // 首次进入且画布为空白时，依据已归档情报自动布置侦探墙
    const state = useStore.getState();
    const hasIntel = state.targets.length > 0 || state.entities.length > 0;
    const elements =
      stored.length === 0 && hasIntel
        ? buildIntelGraph({
            targets: state.targets,
            entities: state.entities,
            relations: state.relations,
            evidence: state.evidence,
            hypotheses: state.hypotheses,
            timeline: state.timeline,
          })
        : stored;

    controller.loadElements(elements);
    savedIdsRef.current = new Set(elements.map(el => el.id));
    if (stored.length === 0 && hasIntel) {
      controller.fitToScreen();
    } else {
      controller.setViewport({
        viewportX: boardViewport.x,
        viewportY: boardViewport.y,
        zoom: boardViewport.zoom,
      });
      if (elements.length === 0) controller.viewport.setCenter([0, 0]);
    }
    setEmpty(elements.length === 0);
    setTimeout(() => controller.requestRender(), 0);
  }, [activeEngagementId, boardLoadedFor, ready]);

  const handleBuildGraph = () => {
    const controller = controllerRef.current;
    if (!controller) return;
    const state = useStore.getState();
    if (state.entities.length === 0 && state.targets.length === 0) {
      state.toast('error', '当前案件下没有可用于布局的情报');
      return;
    }
    const elements = buildIntelGraph({
      targets: state.targets,
      entities: state.entities,
      relations: state.relations,
      evidence: state.evidence,
      hypotheses: state.hypotheses,
      timeline: state.timeline,
    });
    controller.pushHistory();
    controller.loadElements(elements);
    boardCache = { engagementId: state.activeEngagementId ?? '', elements };
    savedIdsRef.current = new Set(elements.map(el => el.id));
    controller.fitToScreen();
    setEmpty(elements.length === 0);
    state.toast('success', `已生成 ${elements.length} 个节点与关联线`);
  };

  /** 从侧栏拖拽线索/对象/事件到墙上。 */
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const controller = controllerRef.current;
    if (!controller) return;
    const payload = readDragPayload(event);
    if (!payload) return;
    const model = controller.viewport.clientToModel([event.clientX, event.clientY]);
    const refKind =
      payload.kind === 'timeline' ? 'evidence' : (payload.kind as 'target' | 'entity' | 'evidence');

    controller.pushHistory();
    const card = controller.addNote([model[0] + 120, model[1] + 80], {
      title: payload.title,
      text: payload.text,
      color: payload.color ?? (payload.kind === 'target' ? 'blue' : 'green'),
      refKind,
      refId: payload.kind === 'timeline' ? `timeline:${payload.id}` : payload.id,
    });
    if (payload.photo) {
      const photo = controller.addPhoto([model[0] - 200, model[1] + 20], payload.photo, payload.title);
      controller.surface.update(photo.id, { attachTo: card.id });
    }
    controller.addPin([model[0] + 120, model[1] + 80], undefined, 'push', card.id);
    useStore.getState().toast('success', '已钉到关联墙');
  };

  return (
    <div
      className="relative min-w-0 flex-1 overflow-hidden"
      onDragOver={event => {
        if (event.dataTransfer.types.includes(DRAG_MIME)) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }
      }}
      onDrop={handleDrop}
    >
      <div className="canvas-root" ref={containerRef}>
        <canvas className="canvas-layer" ref={contentRef} />
        <div className="canvas-dom-layer" ref={domHostRef} />
        {/* 标注层：图钉 / 胶带 / 圈注 / 手绘 / 印章 / 遮挡，压在卡片之上 */}
        <canvas className="canvas-layer canvas-annotations" ref={annotationRef} />
        <canvas className="canvas-layer canvas-overlay" ref={overlayRef} />
      </div>

      {hud.tools && <Toolbar onBuildGraph={handleBuildGraph} />}
      {hud.minimap && <Minimap />}

      {hud.casePlate && <WallCasePlate />}
      {hud.suggestions && <LinkSuggestions />}

      {/* 释放关联墙后的边缘展开条（预览模式下隐藏） */}
      {!preview && !panels.left && (
        <button
          type="button"
          onClick={() => togglePanel('left')}
          title="展开案卷栏　["
          className="pointer-events-auto absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-r-[var(--radius)] border border-l-0 border-border bg-card/95 px-1.5 py-4 text-muted-foreground shadow-md backdrop-blur hover:text-foreground"
        >
          <RiLayoutLeft2Line className="size-4" />
        </button>
      )}
      {!preview && !panels.right && (
        <button
          type="button"
          onClick={() => togglePanel('right')}
          title="展开档案栏　]"
          className="pointer-events-auto absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-l-[var(--radius)] border border-r-0 border-border bg-card/95 px-1.5 py-4 text-muted-foreground shadow-md backdrop-blur hover:text-foreground"
        >
          <RiLayoutRight2Line className="size-4" />
        </button>
      )}

      {/* 底部案情条：状态 + 时间轴带 + 研判码头 */}
      {hud.bottomBar && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex h-9 items-center gap-3 border-t border-border bg-card/92 px-3 backdrop-blur">
          <div className="pointer-events-auto">
            <WallStatusBar />
          </div>
          <div className="h-5 w-px shrink-0 bg-border" />
          <WallTimelineRibbon />
          <div className="h-5 w-px shrink-0 bg-border" />
          <WallAiDock />
        </div>
      )}

      {/* 全屏预览：极简控制条（巡览 + 缩放） */}
      {preview && (
        <div className="pointer-events-auto absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1 rounded-full border border-border bg-card/95 px-2 py-1 shadow-[0_12px_34px_rgba(27,28,30,0.18)] backdrop-blur">
          {(() => {
            const tour = liveController?.tourState ?? { active: false, index: 0, total: 0, title: '', speed: 1, paused: false };
            return (
              <>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title={tour.active ? (tour.paused ? '继续巡航' : '暂停巡航') : '开始自动巡航'}
                  onClick={() => {
                    if (!tour.active) launchTour();
                    else controllerRef.current?.toggleTourPause();
                  }}
                >
                  {tour.active && !tour.paused ? <RiPauseLine className="size-4" /> : <RiPlayLine className="size-4" />}
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="上一站"
                  disabled={!tour.active}
                  onClick={() => controllerRef.current?.tourPrev()}
                >
                  <RiSkipBackLine className="size-4" />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="下一站"
                  disabled={!tour.active}
                  onClick={() => controllerRef.current?.tourNext()}
                >
                  <RiSkipForwardLine className="size-4" />
                </Button>

                <span className="max-w-[190px] truncate px-1 text-[10.5px] text-muted-foreground">
                  {tour.active
                    ? `${tour.index + 1}/${tour.total} · ${tour.title || '巡航中'}`
                    : '未开始巡航'}
                </span>

                <select
                  className="h-6 rounded-[var(--radius-sm)] border border-input bg-card px-1 text-[10.5px]"
                  value={tourSpeed}
                  onChange={event => {
                    const next = Number(event.target.value);
                    setTourSpeed(next);
                    controllerRef.current?.setTourSpeed(next);
                  }}
                  title="巡航速度"
                >
                  <option value={0.6}>0.6×</option>
                  <option value={1}>1×</option>
                  <option value={1.6}>1.6×</option>
                  <option value={2.4}>2.4×</option>
                </select>

                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="停止巡航"
                  disabled={!tour.active}
                  onClick={() => controllerRef.current?.stopTour()}
                >
                  <RiCloseLine className="size-4" />
                </Button>

                <div className="mx-0.5 h-4 w-px bg-border" />

                <Button size="icon-sm" variant="ghost" onClick={() => controllerRef.current?.zoomOut()}>
                  <RiSubtractLine className="size-4" />
                </Button>
                <span className="w-11 text-center font-mono text-[11px]">
                  {Math.round((liveController?.viewport.zoom ?? 1) * 100)}%
                </span>
                <Button size="icon-sm" variant="ghost" onClick={() => controllerRef.current?.zoomIn()}>
                  <RiAddLine className="size-4" />
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  title="适应全图"
                  onClick={() => controllerRef.current?.fitToScreen()}
                >
                  <RiFullscreenLine className="size-4" />
                </Button>

                <div className="mx-0.5 h-4 w-px bg-border" />

                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    controllerRef.current?.stopTour();
                    setPreview(false);
                  }}
                >
                  <RiCloseLine className="size-3.5" />
                  退出预览
                </Button>
                <span className="pr-1 text-[10px] text-muted-foreground">Esc</span>
              </>
            );
          })()}
        </div>
      )}

      {ready && empty && (
        <div className="canvas-empty-hint">
          <div className="pointer-events-auto w-[330px] rounded-[var(--radius-lg)] border border-border bg-card/95 p-5 text-center shadow-[0_18px_50px_rgba(27,28,30,0.14)] backdrop-blur">
            <div className="mx-auto mb-3 grid size-10 place-items-center rounded-[var(--radius)] bg-muted">
              <RiNodeTree className="size-5 text-muted-foreground" />
            </div>
            <div className="text-[13px] font-semibold">关联图空白</div>
            <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
              点击工具栏「生成关联图」按已归档情报自动布局，或直接双击画布手工建立节点。
            </p>
            <div className="mt-3 flex items-center justify-center gap-3 text-[10.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <RiCursorLine className="size-3.5" />
                空格拖拽平移
              </span>
              <span className="inline-flex items-center gap-1">
                <RiStickyNoteLine className="size-3.5" />
                双击新建卡片
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}